
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useRef } from "react";
import { Header } from "@/components/layout/Header";
import { TestHeader } from "@/components/test/TestHeader";
import { TestContainer } from "@/components/test/TestContainer";
import { ProfessionalScorecard } from "@/components/test/ProfessionalScorecard";
import { TestLoading } from "@/components/test/TestLoading";
import { TestError } from "@/components/test/TestError";
import { TestEmpty } from "@/components/test/TestEmpty";
import { useTestData } from "@/hooks/useTestData";
import { supabase } from "@/integrations/supabase/client";
import { Answer } from "@/types/test";
import { scrollToTop } from "@/utils/scrollUtils";

const TestPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [showResults, setShowResults] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [childId, setChildId] = useState<string | null>(null);
  const testContainerRef = useRef<HTMLDivElement>(null);

  const { data: test, isLoading, error } = useTestData(testId);

  useEffect(() => {
    if (location.state?.reset) {
      setCurrentQuestionIndex(0);
      setAnswers({});
      setShowResults(false);
      setIsSubmitting(false);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    const selectedChildId = localStorage.getItem("selectedChildId");
    if (!selectedChildId) {
      toast({
        title: "Error",
        description: "No child selected for this test",
        variant: "destructive",
      });
      navigate("/testing-modules");
      return;
    }
    setChildId(selectedChildId);
  }, [navigate, toast]);

  // Scroll to top on initial load
  useEffect(() => {
    scrollToTop();
  }, []);

  // Scroll to test container when question changes or test loads
  useEffect(() => {
    if (!isLoading && test && testContainerRef.current) {
      testContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentQuestionIndex, isLoading, test]);

  // Scroll to top when showing results
  useEffect(() => {
    if (showResults) {
      scrollToTop();
    }
  }, [showResults]);

  const handleAnswerSelect = (questionId: number, answerIndex: number, value?: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { index: answerIndex, value },
    }));
  };

  const handleNext = () => {
    if (test && currentQuestionIndex < test.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!test || !childId) return;

    try {
      setIsSubmitting(true);
      const score = calculateScore();

      const formattedAnswers: Record<string, number | string> = {};
      Object.entries(answers).forEach(([questionId, answer]) => {
        formattedAnswers[questionId] = answer.value || answer.index;
      });

      const testResult = {
        test_id: test.id,
        child_id: childId,
        score: score,
        answers: formattedAnswers,
      };

      const { error: resultError } = await supabase
        .from("test_results")
        .insert(testResult);

      if (resultError) throw resultError;

      setShowResults(true);
    } catch (error: any) {
      console.error("Error submitting test:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit test",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateScore = () => {
    if (!test) return 0;
    let score = 0;
    let totalQuestions = 0;

    test.questions.forEach(question => {
      if (question.correctAnswer !== null) {
        totalQuestions++;
        const answer = answers[question.id];
        if (answer) {
          const isCorrect = typeof question.correctAnswer === 'string'
            ? question.options[answer.index] === question.correctAnswer
            : answer.index === question.correctAnswer;
            
          if (isCorrect) {
            score++;
          }
        }
      }
    });

    return totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
  };

  if (isLoading) {
    return <TestLoading />;
  }

  if (error) {
    return <TestError error={error as Error} />;
  }

  if (!test || !test.questions || test.questions.length === 0) {
    return <TestEmpty />;
  }

  if (showResults) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <ProfessionalScorecard
            score={calculateScore()}
            category={test.category}
            difficulty={test.difficulty}
            testId={test.id}
            questions={test.questions}
            answers={answers}
          />
        </main>
      </div>
    );
  }

  const currentQuestion = test.questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <TestHeader />
        <div ref={testContainerRef}>
          <TestContainer
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={test.questions.length}
            currentQuestion={currentQuestion}
            currentAnswer={currentQuestion ? answers[currentQuestion.id] : undefined}
            isSubmitting={isSubmitting}
            ageCategory={test.grade_level}
            category={test.category}
            stage={test.title}
            onAnswerSelect={handleAnswerSelect}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onSubmit={handleSubmit}
          />
        </div>
      </main>
    </div>
  );
};

export default TestPage;
